import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Trash2, X } from "lucide-react";
import { useDeleteOneOnOneSetup } from "@/hooks/api-hooks";

interface OneOnOneSetup {
  id: string;
  title?: string;
  participantId: string;
  managerId: string;
  frequency: 'weekly' | 'bi-weekly' | 'monthly';
  duration: number;
  defaultAgenda: string[];
  isActive: boolean;
}

interface TeamMember {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'owner' | 'admin' | 'manager' | 'staff';
}

interface DeleteOneOnOneSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  setup: OneOnOneSetup | null;
  teamMembers: TeamMember[];
  onSuccess?: () => void;
}

export function DeleteOneOnOneSetupModal({
  isOpen,
  onClose,
  teamId,
  setup,
  teamMembers,
  onSuccess
}: DeleteOneOnOneSetupModalProps) {
  const deleteSetupMutation = useDeleteOneOnOneSetup();

  const handleDelete = () => {
    if (!setup) return;

    deleteSetupMutation.mutate(
      { setupId: setup.id, teamId },
      {
        onSuccess: () => {
          const participant = teamMembers.find(m => m.user.id === setup.participantId);
          const manager = teamMembers.find(m => m.user.id === setup.managerId);
          toast.success(`1-on-1 setup deleted for ${participant?.user.firstName} & ${manager?.user.firstName}`);

          onSuccess?.();
          onClose();
        },
        onError: (error: any) => {
          console.error("Error deleting 1-on-1 setup:", error);
          toast.error(`Failed to delete setup: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  if (!setup) return null;

  const participant = teamMembers.find(m => m.user.id === setup.participantId);
  const manager = teamMembers.find(m => m.user.id === setup.managerId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            Delete 1-on-1 Setup
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete this 1-on-1 setup? This action cannot be undone.
            </p>

            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="space-y-2">
                {setup.title && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Setup Name</span>
                    <span className="font-medium">{setup.title}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Participants</span>
                  <span className="font-medium">
                    {participant ? `${participant.user.firstName} ${participant.user.lastName}` : 'Unknown'} & {manager ? `${manager.user.firstName} ${manager.user.lastName}` : 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Frequency</span>
                  <span className="font-medium capitalize">{setup.frequency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Duration</span>
                  <span className="font-medium">{setup.duration} minutes</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className={`font-medium ${setup.isActive ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {setup.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {setup.defaultAgenda.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-sm text-muted-foreground">Default Agenda ({setup.defaultAgenda.length} items)</span>
                    <div className="text-sm">
                      {setup.defaultAgenda.slice(0, 2).map((item, index) => (
                        <div key={index} className="text-muted-foreground">• {item}</div>
                      ))}
                      {setup.defaultAgenda.length > 2 && (
                        <div className="text-xs text-muted-foreground">+ {setup.defaultAgenda.length - 2} more items</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-red-800">Warning</div>
                  <div className="text-sm text-red-700">
                    Deleting this setup will also remove any scheduled sessions associated with it. This action cannot be undone.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={deleteSetupMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteSetupMutation.isPending}
              className="gap-2"
            >
              {deleteSetupMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-background"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Setup
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
